/**
 * A second copy of every local change, outside RxDB.
 *
 * The app is offline-first: a Befund written in a trench exists on one tablet
 * and nowhere else until a push accepts it. Everything that keeps it — schema,
 * migrations, storage adapter — belongs to RxDB, so a schema update that goes
 * wrong takes the app's ability to read its own data with it, and the only
 * recovery on offer wipes the lot. So each local write also lands here: the
 * whole document, as JSON, in its own IndexedDB database with one object store
 * and no schema worth the name — nothing a version bump could fail to migrate,
 * reachable when RxDB is not.
 *
 * It holds only what has NOT gone up yet; an entry is dropped the moment a push
 * is accepted, like the author journal beside it (`db/authorJournal`), which
 * says WHICH documents are outstanding and who wrote them where this says WHAT
 * they contain.
 *
 * Not localStorage: five megabytes for the whole origin, written synchronously
 * on the thread drawing the form, and it throws when full — at the one moment a
 * write must not be lost.
 */

const DB_NAME = 'ast_outbox';
const DB_VERSION = 1;
const STORE = 'docs';

/** One local change, kept until the server has it. */
export interface OutboxEntry {
    collection: string;
    docId: string;
    /** The document as RxDB holds it, `_rev` and `_deleted` included. */
    doc: Record<string, unknown>;
    savedAt: number;
    /**
     * The collection's schema version at the write, so a rescue sent by a
     * later build is lifted from the right shape. Absent on older entries.
     */
    schemaVersion?: number;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
    if (!dbPromise) {
        dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);
            request.onupgradeneeded = () => {
                const db = request.result;
                if (!db.objectStoreNames.contains(STORE)) {
                    db.createObjectStore(STORE);
                }
            };
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => {
                // Not kept: a refusal can pass, and a safety net that stayed
                // off for the rest of the session would never say so.
                dbPromise = null;
                reject(request.error);
            };
        });
    }
    return dbPromise;
}

/** `collection/docId` — one entry per document, the newest state winning. */
function keyOf(collection: string, docId: string): string {
    return `${collection}/${docId}`;
}

function run<T>(request: IDBRequest<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
}

/**
 * Keep a local change. Never throws and never blocks the write it shadows: a
 * failing safety net must not become the reason a Befund cannot be saved.
 */
export async function keepInOutbox(
    collection: string,
    docId: string,
    doc: Record<string, unknown>,
    schemaVersion: number
): Promise<void> {
    if (docId === '') {
        return;
    }
    try {
        const db = await openDb();
        const tx = db.transaction(STORE, 'readwrite');
        const entry: OutboxEntry = {
            collection,
            docId,
            // Structured-cloned by IndexedDB, so the copy cannot be mutated
            // afterwards by whoever still holds the document.
            doc: JSON.parse(JSON.stringify(doc)) as Record<string, unknown>,
            savedAt: Date.now(),
            schemaVersion
        };
        tx.objectStore(STORE).put(entry, keyOf(collection, docId));
        await new Promise<void>((resolve) => {
            tx.oncomplete = () => resolve();
            tx.onerror = () => resolve();
            tx.onabort = () => resolve();
        });
    } catch {
        // Storage refused, or IndexedDB is unavailable. The document is still
        // in RxDB; this copy is the belt, not the trousers.
    }
}

/** Forget documents the server has accepted. Best effort, like the keeping. */
export async function dropFromOutbox(
    collection: string,
    docIds: readonly string[]
): Promise<void> {
    if (docIds.length === 0) {
        return;
    }
    try {
        const db = await openDb();
        const tx = db.transaction(STORE, 'readwrite');
        const store = tx.objectStore(STORE);
        for (const docId of docIds) {
            store.delete(keyOf(collection, docId));
        }
        await new Promise<void>((resolve) => {
            tx.oncomplete = () => resolve();
            tx.onerror = () => resolve();
            tx.onabort = () => resolve();
        });
    } catch {
        // An entry that outlives its push is swept up by the salvage side,
        // which checks each document against the server before it acts.
    }
}

/** Everything still waiting. Empty when the store cannot be read at all. */
export async function readOutbox(): Promise<OutboxEntry[]> {
    try {
        const db = await openDb();
        const tx = db.transaction(STORE, 'readonly');
        const all = await run(tx.objectStore(STORE).getAll());
        return (all as OutboxEntry[]).filter(
            (entry) =>
                entry && typeof entry.collection === 'string' && entry.doc
        );
    } catch {
        return [];
    }
}

/** How many changes the outbox is holding, without reading them all out. */
export async function outboxCount(): Promise<number> {
    try {
        const db = await openDb();
        const tx = db.transaction(STORE, 'readonly');
        return await run(tx.objectStore(STORE).count());
    } catch {
        return 0;
    }
}
